import { useEffect } from "react";

function upsertMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertJsonLd(id, payload) {
  if (!payload) return;
  let el = document.head.querySelector(`script[data-jsonld="${id}"]`);
  if (!el) {
    el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute("data-jsonld", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(payload);
}

const DEFAULT_OG =
  "https://images.pexels.com/photos/8569166/pexels-photo-8569166.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

/**
 * Minimal SEO head manager. Mutates document.head directly so we don't need
 * react-helmet. Apply once per page route.
 */
export default function SeoHead({
  title,
  description,
  keywords,
  image = DEFAULT_OG,
  url,
  jsonLd,
  noindex = false,
}) {
  useEffect(() => {
    const currentUrl = url || (typeof window !== "undefined" ? window.location.href : "");
    if (title) document.title = title;
    if (description) upsertMeta("name", "description", description);
    if (keywords) upsertMeta("name", "keywords", keywords);
    upsertMeta("name", "robots", noindex ? "noindex,nofollow" : "index,follow");
    // Open Graph
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:image", image);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:url", currentUrl);
    upsertMeta("property", "og:site_name", "Apex MVA");
    // Twitter
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", image);
    // Canonical
    upsertLink("canonical", currentUrl);
    // JSON-LD
    if (jsonLd) upsertJsonLd("page", jsonLd);
  }, [title, description, keywords, image, url, jsonLd, noindex]);
  return null;
}

// Shared LegalService schema (firm-wide). Inject on landing + city pages.
export const firmJsonLd = () => ({
  "@context": "https://schema.org",
  "@type": "LegalService",
  name: "Apex MVA Legal Group",
  description:
    "Motor vehicle accident lawyers serving New York and New Jersey. Free case review. No fee unless we win. Hablamos español.",
  areaServed: [
    { "@type": "State", name: "New York" },
    { "@type": "State", name: "New Jersey" },
  ],
  telephone: "+1-800-555-1234",
  priceRange: "Contingency — no upfront cost",
  address: {
    "@type": "PostalAddress",
    addressRegion: "NY",
    addressCountry: "US",
  },
  url: typeof window !== "undefined" ? window.location.origin : "",
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
    opens: "00:00",
    closes: "23:59",
  },
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.9",
    reviewCount: "12400",
  },
});

export const faqJsonLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: items.map((i) => ({
    "@type": "Question",
    name: i.q,
    acceptedAnswer: { "@type": "Answer", text: i.a },
  })),
});
